// src/configs/applications/applekkus.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import {
  AuditOutlined,
  CalendarOutlined,
  DollarOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  FormOutlined,
  FundProjectionScreenOutlined,
  FunnelPlotOutlined,
  GlobalOutlined,
  RocketOutlined,
  SearchOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  ToolOutlined,
  MessageOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { BsBoxSeam, BsBuilding } from 'react-icons/bs';
import { RiListSettingsLine, RiShieldUserLine } from 'react-icons/ri';
import { GrGroup } from 'react-icons/gr';
import { PiToolbox, PiUserFocus } from 'react-icons/pi';
import { TbTruckDelivery } from 'react-icons/tb';
import { MdOutlineBadge } from 'react-icons/md';

// *******************************************************************************
// APP GLOBAL CONFIGURATION
// *******************************************************************************
const APP_LEKKUS = {
  ID: 'applekkus',
  NAME: 'Lekkus SaaS',
  THEME_COLOR: 'CUSTOM_LEKKUS_APP',
  THEME: 'light',
  FINTECH_SERVICES: true,
  DOMAINS: [
    'sistema.lekkus.com',
    'sistema.lekkus.com.br',
    'applekkus-v2.web.app',
    'homologacao.lekkus.com',
    'homologacao.lekkus.com.br',
    'applekkus-v2-homologation.web.app',
  ],
  PRESET: {
    MULTI_SUBSCRIPTIONS: true,
    PROFILE: null,
    NATIVE_RESOURCES: [],
    SUGGESTED_RESOURCES: ['moduleresources_lookup'],
    CAN_CHANGE_RESOURCES: true,
    SHOW_RESOURCES_FIRST_CONFIGURATION: true,
    SHOW_RESOURCES_SUBSCRIPTION: true,
    CONTRACT: null,
  },
  COPY_RIGHT: 'Lekkus SaaS',
  API_PORT_HOMOLOGATION: 8112,
  API_PORT_PRODUCTION: 8012,
  API_HOST_LOCALHOST: 'localhost',
  API_HOST_PRODUCTION: 'vps.jarvisly.com',
  API_INTEGRATED: false,
  TOKEN: import.meta.env.VITE_APPLEKKUS_TOKEN,
  TOKEN_EXP_MINUTES: 60,

  LOCALE: 'pt-BR',
  // LOCALE: 'en-US',
  LANGUAGE: 'pt-BR',
  // LANGUAGE: 'en-US',
  LANGUAGE_OPTIONS: ['pt-BR', 'pt-PT', 'en-US', 'es-ES'],

  LOGO_APP: '/apps/applekkus/logo-app.png',
  LOGO_APP_WHITE: '/apps/applekkus/logo-app-white.png',

  LOGO_APP_ICON: '/apps/applekkus/logo-app-icon.png',
  LOGO_APP_ICON_WHITE: '/apps/applekkus/logo-app-icon-white.png',

  LOGO: '/apps/applekkus/logo.png',
  LOGO_WHITE: '/apps/applekkus/logo-white.png',
  LOGO_LOGIN_SIDE: '/apps/applekkus/logo-app-side.png',

  PWA_NAME: { production: 'Lekkus ERP', homologation: 'Lekkus ERP Test' },
  PWA_SHORT_NAME: { production: 'Lekkus', homologation: 'Lekkus Test' },
  PWA_DESCRIPTION: 'ERP SaaS for business management',
  TITLE: 'Lekkus SaaS',
  SLOGAN: '',
  SLOGAN2: '',
  SLOGAN_REGISTRATION: 'registration_slogan',
  SLOGAN_REGISTRATION2: '',
  APPLICATION_LAYER: 'adm',
  LOGIN_IDENTIFICATION: 'email',

  // SELF_REGISTRATION can be:
  // - boolean: true/false (same for all environments)
  // - object: { homologation: true/false, production: true/false } (different per environment)
  SELF_REGISTRATION: {
    homologation: true,
    production: false,
  },
  // MOBILE_VALIDATION can be:
  // - false: never asks for mobile
  // - true or []: asks for mobile but doesn't send SMS
  // - ['pt-BR', 'en-US', ...]: asks for mobile and sends SMS for specified countries (BCP 47 format)
  // - object: { homologation: value, production: value } for different behavior per environment
  SELF_REGISTRATION_MOBILE_VALIDATION: ['pt-BR', 'pt-PT'],
  USER_INVITATION_MOBILE_VALIDATION: ['pt-BR', 'pt-PT'],

  GOOGLE_LOGIN: false,
  FACEBOOK_LOGIN: false,
  RESET_PASSWORD: true,
  NAVIGATION_TYPE: 'side', // 'side', 'top' or 'tile'
  MODULES: [
    'customers',
    'suppliers',
    'collaborators',
    'companies',
    'products',
    'services',
    'toolbox',
    'orders',
    'invoices',
    'fiscalConfig',
    'dataSearch',
    'crm',
    'serviceOrders',
    'landingPages',
    'leads',
    'omnichannel',
    'goodsReceipt',
    'ticketEvents',
  ],
  SHOW_ALL_MENUS: false,
  BREADCRUMB_COLOR: '#FAFAFBFF',

  WELCOME_NEXT_STEPS: [
    'explore_the_system',
    'complete_your_profile',
    'create_your_first_subscription',
    // 'invite_your_team',
  ],

  MENU_APPLICATION: [
    {
      path: '/app/account',
      title: 'account_settings',
      icon: SettingOutlined,
      moduleId: 'account',
    },
  ],

  MENU_NAVIGATION: [
    // ******************
    // DASHBOARD
    // ******************
    {
      path: '/app/dashboard/overview',
      title: 'home',
      icon: HomeOutlined,
      moduleId: 'welcome',
      isGroupTitle: true,
    },

    // ******************
    // CADASTROS
    // ******************
    {
      path: '/app/registrations',
      title: 'records',
      icon: FormOutlined,
      isGroupTitle: true,
      submenu: [
        {
          title: 'companies',
          icon: BsBuilding,
          moduleId: 'companies',
          requiredResources: ['moduleresources_companies'],
        },
        {
          title: 'suppliers',
          icon: TbTruckDelivery,
          moduleId: 'suppliers',
          requiredResources: ['moduleresources_suppliers'],
        },
        {
          title: 'collaborators',
          icon: MdOutlineBadge,
          moduleId: 'collaborators',
          requiredResources: ['moduleresources_collaborators'],
        },
        {
          title: 'customers',
          icon: GrGroup,
          moduleId: 'customers',
          requiredResources: ['moduleresources_customers'],
        },
        { type: 'divider' },
        {
          title: 'ticket_events',
          icon: CalendarOutlined,
          moduleId: 'ticketEvents',
          requiredResources: ['moduleresources_ticket_events'],
        },
        {
          path: '/app/registrations/products',
          title: 'products',
          icon: BsBoxSeam,
          moduleId: 'products',
          requiredResources: ['moduleresources_products'],
        },
        {
          path: '/app/registrations/services',
          title: 'services',
          icon: ToolOutlined,
          moduleId: 'services',
          requiredResources: ['moduleresources_services'],
        },
        {
          path: '/app/registrations/toolbox',
          title: 'toolbox',
          icon: PiToolbox,
          // moduleId: 'toolbox',
          moduleId: null,
          requiredResources: ['moduleresources_toolbox'],
        },
        {
          path: '/app/registrations/assets',
          title: 'assets',
          icon: ToolOutlined,
          moduleId: null,
          requiredResources: ['moduleresources_assets'],
        },
        // {
        //   title: 'users',
        //   icon: RiShieldUserLine,
        //   moduleId: 'users',
        //   requiredResources: ['moduleresources_users'],
        // },
      ],
    },

    // ******************
    // COMPRAS
    // ******************
    {
      path: '/app/purchases',
      title: 'purchases',
      icon: ShoppingCartOutlined,
      isGroupTitle: true,
      submenu: [
        {
          path: '/app/purchases/purchase-requests',
          title: 'requests',
          icon: FormOutlined,
          moduleId: null,
          requiredResources: ['moduleresources_purchases'],
        },
        {
          path: '/app/purchases/purchase-orders',
          title: 'orders',
          icon: ShoppingCartOutlined,
          moduleId: null,
          requiredResources: ['moduleresources_purchases'],
        },
        {
          title: 'goods_receipt',
          icon: FileTextOutlined,
          moduleId: 'goodsReceipt',
          requiredResources: ['moduleresources_purchases'],
        },
      ],
    },

    // ******************
    // MARKETING
    // ******************
    {
      path: '/app/marketing',
      title: 'marketing',
      icon: FundProjectionScreenOutlined,
      isGroupTitle: true,
      submenu: [
        {
          title: 'landing_pages',
          icon: GlobalOutlined,
          moduleId: 'landingPages',
          requiredResources: ['moduleresources_landingpages'],
        },
        {
          title: 'leads',
          icon: PiUserFocus,
          moduleId: 'leads',
          requiredResources: ['moduleresources_leads'],
        },
      ],
    },

    // ******************
    // COMERCIAL
    // ******************
    {
      path: '/app/commercial',
      title: 'commercial',
      icon: RocketOutlined,
      isGroupTitle: true,
      submenu: [
        {
          title: 'crm',
          icon: FunnelPlotOutlined,
          moduleId: 'crm',
          requiredResources: ['moduleresources_crm'],
        },
        {
          title: 'service_orders',
          icon: PiToolbox,
          moduleId: 'serviceOrders',
          requiredResources: ['moduleresources_services'],
        },
        // {
        //   title: 'quotes_and_orders',
        //   icon: PiToolbox,
        //   moduleId: 'orders',
        //   requiredResources: ['moduleresources_sales'],
        // },
        {
          path: '/app/commercial/sales-orders',
          title: 'sales_orders',
          icon: FileTextOutlined,
          moduleId: null,
          requiredResources: ['moduleresources_sales_orders'],
        },
        {
          path: '/app/commercial/pos',
          title: 'pos',
          icon: ShoppingCartOutlined,
          moduleId: null,
          requiredResources: ['moduleresources_pos'],
        },
        {
          path: '/app/commercial/contracts',
          title: 'contracts',
          icon: FileProtectOutlined,
          moduleId: null,
          requiredResources: ['moduleresources_contracts'],
        },
      ],
    },

    // ******************
    // OPERACIONAL
    // ******************
    {
      path: '/app/operational',
      title: 'operational',
      icon: ToolOutlined,
      isGroupTitle: true,
      submenu: [
        {
          title: 'data_search',
          icon: SearchOutlined,
          moduleId: 'dataSearch',
          requiredResources: ['moduleresources_lookup'],
        },
        {
          title: 'omnichannel',
          icon: MessageOutlined,
          moduleId: 'omnichannel',
          requiredResources: ['moduleresources_omnichannel'],
        },
        {
          path: '/app/operational/inventory',
          title: 'inventory_control',
          icon: BsBoxSeam,
          moduleId: null,
          requiredResources: ['moduleresources_inventory'],
        },
      ],
    },

    // ******************
    // FATURAMENTO
    // ******************
    {
      path: '/app/billing',
      title: 'billing',
      icon: FileTextOutlined,
      isGroupTitle: true,
      submenu: [
        {
          title: 'invoices',
          icon: FileProtectOutlined,
          moduleId: 'invoices',
          requiredResources: ['moduleresources_nfe', 'moduleresources_nfce'],
        },
        {
          path: '/app/billing/service-invoices',
          title: 'service_invoices',
          icon: AuditOutlined,
          moduleId: null,
          requiredResources: ['moduleresources_nfse'],
        },
        {
          title: 'fiscal_settings',
          icon: RiListSettingsLine,
          moduleId: 'fiscalConfig',
          requiredResources: [
            'moduleresources_nfe',
            'moduleresources_nfce',
            'moduleresources_nfse',
          ],
        },
      ],
    },

    // ******************
    // FINANCEIRO
    // ******************
    {
      path: '/app/financial',
      title: 'financial',
      icon: DollarOutlined,
      isGroupTitle: true,
      submenu: [
        {
          path: '/app/financial/receivables',
          title: 'receivables',
          icon: DollarOutlined,
          moduleId: null,
          requiredResources: ['moduleresources_receivables'],
        },
        {
          path: '/app/financial/payables',
          title: 'payables',
          icon: DollarOutlined,
          moduleId: null,
          requiredResources: ['moduleresources_payables'],
        },
        {
          path: '/app/financial/cashier',
          title: 'cashier',
          icon: DollarOutlined,
          moduleId: null,
          requiredResources: ['moduleresources_cashier'],
        },
        { type: 'divider' },
        {
          path: '/app/financial/bank-reconciliation',
          title: 'bank_reconciliation',
          icon: AuditOutlined,
          moduleId: null,
          requiredResources: ['moduleresources_bank_reconciliation'],
        },
        {
          path: '/app/financial/cash-flow',
          title: 'cash_flow',
          icon: FundProjectionScreenOutlined,
          moduleId: null,
          requiredResources: ['moduleresources_cash_flow'],
        },
        {
          path: '/app/financial/dre',
          title: 'dre',
          icon: FileTextOutlined,
          moduleId: null,
          requiredResources: ['moduleresources_dre'],
        },
      ],
    },
  ],
  FIREBASE_CONFIG: JSON.parse(import.meta.env.VITE_APPLEKKUS_FIREBASE_CONFIG),
  MOCKUP: {
    AUTH: {
      SIGNIN: {
        email: 'me@biruel.dev',
        password: 'idadm123',
      },
      SIGNUP: {
        // step 0
        countryCode: 'BR',
        // countryCode: 'US',
        name: 'Suporte Lekkus',
        email: 'me@biruel.dev',

        // step 1
        emailCode: 1234,
        emailConfirmation: true,

        // step 2
        // phone: {
        //   value: '19982472235',
        //   countryCode: 'BR',
        //
        //   // value: '6509849382',
        //   // countryCode: 'US',
        // },

        // step 3
        mobileCode: 1234,
        mobileConfirmation: true,

        // step 4
        password: 'idadm123',
      },
      RESET_PASSWORD: {
        // step 0
        email: 'me@biruel.dev',

        // step 1
        emailCode: 1234,

        // step 2
        password: 'idadm123',
      },
    },
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default APP_LEKKUS;
